// Sorte de feuille « grandeurs et unités » (lot M5) — mécanisme général du noyau ; les unités et leurs natures
// sont des DONNÉES (pack `grandeurs` : `units`, `dimensions`).
//
// Une ligne = une chaîne de grandeurs reliées par « = » (« 6 cm × 8 cm = 48 cm² », « 72000 cm³ = 72 L »,
// « 10 h 20 min − 8 h 50 min = 9 h 80 min − 8 h 50 min »). Chaque maillon doit être juste : même nature (même
// dimension) et même valeur, une fois tout ramené aux unités de base. Des grandeurs accolées (« 2 h 45 min ») sont
// une somme. Le juge (judge.js) justifie chaque maillon : même écriture d'unités → « Calculer », sinon « Convertir ».

import { isLeaf, symbols } from './ast.js';
import { evaluate, isRelation } from './equiv.js';

const SUP = '²³';

/** Unités connues du moteur (null si aucun pack n'en déclare). */
export function unitsOf(engine) {
  return engine.quantities?.units ?? null;
}

/** Noms d'unités à lire comme un seul nom (plusieurs lettres, ou un signe comme « € »). */
export function unitIdentifiers(units) {
  return Object.keys(units).filter((n) => n.length > 1 || !/^\p{L}$/u.test(n));
}

// « 10 h 20 min » → « (10 h + 20 min) » : des grandeurs accolées (nombre + unité, séparées d'espaces) sont une somme.
export function groupJuxtaposed(text, units) {
  const names = Object.keys(units)
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&'));
  if (!names.length) return text;
  const q = `\\d+(?:[,.]\\d+)?\\s*(?:${names.join('|')})(?![\\p{L}])[${SUP}]?`;
  const re = new RegExp(`(?<![\\p{L}\\d,.])(${q})((?:\\s+${q})+)`, 'gu');
  const grouped = text.replace(
    re,
    (m, first, rest) => `(${[first, ...rest.trim().split(/\s+(?=\d)/u)].join(' + ')})`,
  );
  // unité composée écrite sans espace (« €/L », « km/h ») : un seul bloc, « 1,76 €/L » = 1,76 × (€ ÷ L)
  const u = `(?:${names.join('|')})[${SUP}]?`;
  return grouped.replace(new RegExp(`(?<![\\p{L}])(${u})/(${u})(?![\\p{L}])`, 'gu'), '($1 ÷ $2)');
}

// ── Valeur d'une grandeur ─────────────────────────────────────────────────

const dimKey = (d) =>
  Object.entries(d)
    .filter(([, e]) => e)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([k, e]) => `${k}${e}`)
    .join('·');
const addDim = (a, b, s = 1) => {
  const out = { ...a };
  for (const [k, e] of Object.entries(b)) out[k] = (out[k] ?? 0) + s * e;
  for (const k of Object.keys(out)) if (!out[k]) delete out[k];
  return out;
};
const toNum = (v) =>
  typeof v === 'number' ? v : v && v.q !== undefined ? Number(v.p) / Number(v.q) : NaN;

/**
 * Valeur d'une expression de grandeurs, ramenée aux unités de base : { v, dim } ; { mismatch: [dimA, dimB] } si on
 * ajoute deux natures différentes ; null si l'expression n'est pas une grandeur (lettre inconnue, fonction…).
 */
export function quantityOf(n, units, table) {
  if (n.t === 'Num') return { v: toNum(n), dim: {} };
  if (n.t === 'Sym') {
    if (units[n.name]) return { v: units[n.name].factor, dim: units[n.name].dim };
    const c = table.syntax.constants?.[n.name];
    if (c) {
      const v = toNum(evaluate(n, {}, table));
      return Number.isFinite(v) ? { v, dim: {} } : null;
    }
    return null;
  }
  if (isLeaf(n)) return null;
  const ev = table.nodes[n.t]?.eval;
  const ks = n.k.map((c) => quantityOf(c, units, table));
  const bad = ks.find((k) => !k || k.mismatch);
  if (bad !== undefined) return bad ?? null;
  if (ev === 'add') {
    const d0 = dimKey(ks[0].dim);
    const other = ks.find((k) => dimKey(k.dim) !== d0);
    if (other) return { mismatch: [ks[0].dim, other.dim] };
    return { v: ks.reduce((s, k) => s + k.v, 0), dim: ks[0].dim };
  }
  if (ev === 'neg') return { v: -ks[0].v, dim: ks[0].dim };
  if (ev === 'mul')
    return ks.reduce((a, k) => ({ v: a.v * k.v, dim: addDim(a.dim, k.dim) }), { v: 1, dim: {} });
  if (ev === 'div') return { v: ks[0].v / ks[1].v, dim: addDim(ks[0].dim, ks[1].dim, -1) };
  if (ev === 'pow') {
    const e = ks[1];
    if (dimKey(e.dim) !== '' || !Number.isFinite(e.v)) return null;
    if (dimKey(ks[0].dim) !== '' && !Number.isInteger(e.v)) return null;
    const dim = {};
    for (const [k, x] of Object.entries(ks[0].dim)) dim[k] = x * e.v;
    return { v: ks[0].v ** e.v, dim };
  }
  if (ev === 'sqrt') {
    if (dimKey(ks[0].dim) !== '') return null;
    return { v: Math.sqrt(ks[0].v), dim: {} };
  }
  if (ks.every((k) => dimKey(k.dim) === '')) {
    const v = toNum(evaluate(n, {}, table));
    return Number.isFinite(v) ? { v, dim: {} } : null;
  }
  return null;
}

export const sameDim = (a, b) => dimKey(a) === dimKey(b);
export const sameQuantity = (a, b) =>
  sameDim(a.dim, b.dim) &&
  Math.abs(a.v - b.v) <= 1e-9 * Math.max(1e-12, Math.abs(a.v), Math.abs(b.v));

/** Nom d'une nature (« une aire ») d'après les données du pack, ou une description générique. */
export function dimensionName(dim, engine) {
  const d = (engine.quantities?.dimensions ?? []).find((x) => sameDim(x.dim, dim));
  return d ? d.name : 'une autre grandeur';
}

/** Écriture des unités d'une expression : unité → exposant (sommes : l'ensemble des unités). */
export function unitSignature(n, units, table, s = 1, out = new Map()) {
  if (n.t === 'Sym' && units[n.name]) {
    out.set(n.name, (out.get(n.name) ?? 0) + s);
    return out;
  }
  if (isLeaf(n)) return out;
  const ev = table.nodes[n.t]?.eval;
  if (ev === 'div') {
    unitSignature(n.k[0], units, table, s, out);
    unitSignature(n.k[1], units, table, -s, out);
    return out;
  }
  if (ev === 'pow' && n.k[1].t === 'Num') {
    unitSignature(n.k[0], units, table, s * toNum(n.k[1]), out);
    return out;
  }
  if (ev === 'add') {
    // une somme de grandeurs : l'ensemble de ses unités (« 2 h + 45 min »), sans compter deux fois
    const inner = new Map();
    for (const c of n.k) for (const [u, e] of unitSignature(c, units, table)) inner.set(u, e);
    for (const [u, e] of inner) out.set(u, (out.get(u) ?? 0) + s * e);
    return out;
  }
  for (const c of n.k) unitSignature(c, units, table, s, out);
  return out;
}
export const signatureKey = (m) =>
  [...m]
    .filter(([, e]) => e)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([u, e]) => `${u}^${e}`)
    .join(' ');

/** Une grandeur ou un nombre seul (« 72 L », « 1 h + 30 min », « 2,5 €/kg ») : plus rien à calculer. */
export function isUnitsOnly(n, units) {
  return [...symbols(n)].every((s) => units[s]);
}

/** Facteur entre deux valeurs (diagnostic) : 10ⁿ, 60, ou null. */
export function ratioHint(a, b) {
  if (!(a > 0 && b > 0)) return null;
  const r = a / b,
    k = Math.round(Math.log10(r));
  if (k !== 0 && Math.abs(r / 10 ** k - 1) < 1e-9)
    return { factor: 10 ** Math.abs(k), bigger: k > 0 };
  for (const f of [60, 3600, 24]) {
    if (Math.abs(r / f - 1) < 1e-9) return { factor: f, bigger: true };
    if (Math.abs(r * f - 1) < 1e-9) return { factor: f, bigger: false };
  }
  if (Math.abs(r / (100 / 60) - 1) < 1e-9 || Math.abs(r / 0.6 - 1) < 1e-9) return { minutes: true };
  return null;
}

/** Est-ce une relation ? (réexport pratique pour le juge) */
export { isRelation };
